import React, { useEffect, useState } from 'react';
import {
  collection, getDocs, doc, getDoc, addDoc, updateDoc, query, where, serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

const DIMENSION_GROUPS = [
  {
    id: 'market',
    label: 'Market & Customer',
    color: '#1d4ed8',
    questionIds: ['q01', 'q03', 'q04', 'q05'],
    description: 'Problem definition, target market, value proposition, product-market fit',
  },
  {
    id: 'business',
    label: 'Business Model & Revenue',
    color: '#166534',
    questionIds: ['q02', 'q06', 'q07', 'q11'],
    description: 'Solution fit, customer retention, revenue model, financial management',
  },
  {
    id: 'operations',
    label: 'Operations & Team',
    color: '#0369a1',
    questionIds: ['q08', 'q09', 'q13', 'q14'],
    description: 'Partnerships, team capability, technology infrastructure, operational efficiency',
  },
  {
    id: 'growth',
    label: 'Growth & Impact',
    color: '#7c3aed',
    questionIds: ['q10', 'q12', 'q15', 'q16'],
    description: 'Regional penetration, impact measurement, fundraising readiness, strategic clarity',
  },
];

export default function RubricEditor({ cohortId, allCohorts = [] }) {
  const [baseTemplate, setBaseTemplate]     = useState(null);
  const [variants, setVariants]             = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionDraft, setQuestionDraft]   = useState(null);
  const [loading, setLoading]               = useState(true);
  const [saving, setSaving]                 = useState(false);
  const [saved, setSaved]                   = useState(false);
  const [showNewVariant, setShowNewVariant] = useState(false);
  const [assignTargetCohortId, setAssignTargetCohortId] = useState(cohortId || '');
  const [assignSuccess, setAssignSuccess] = useState(false);
  const [newVariantName, setNewVariantName] = useState('');
  const [newVariantContext, setNewVariantContext] = useState('');
  const [expandedGroup, setExpandedGroup]   = useState('market');

  useEffect(() => {
    setAssignTargetCohortId(cohortId || '');
  }, [cohortId]);

  useEffect(() => {
    async function load() {
      try {
        // Load base template
        const baseDoc = await getDoc(doc(db, 'rubricTemplates', 'tmpl_prog_rfc_ethiopia_2026'));
        if (baseDoc.exists()) setBaseTemplate({ id: baseDoc.id, ...baseDoc.data() });

        // Load all variants
        const snap = await getDocs(
          query(collection(db, 'rubricTemplates'), where('isVariant', '==', true))
        );
        const variantList = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setVariants(variantList);

        // If cohort has a rubricTemplateId, select that variant
        if (cohortId) {
          const cohortDoc = await getDoc(doc(db, 'cohorts', cohortId));
          const cohortRubricId = cohortDoc.data()?.rubricTemplateId;
          if (cohortRubricId && cohortRubricId !== 'tmpl_prog_rfc_ethiopia_2026') {
            const v = variantList.find(v => v.id === cohortRubricId);
            if (v) setSelectedVariant(v);
          }
        }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    }
    load();
  }, [cohortId]);

  // Get effective questions — base + variant overrides
  const getEffectiveQuestions = () => {
    if (!baseTemplate) return [];
    return baseTemplate.questions.map(q => {
      const override = selectedVariant?.questionOverrides?.[q.id];
      return override ? { ...q, ...override, isOverridden: true } : q;
    });
  };

  const getQuestionsForGroup = (group) => {
    const effective = getEffectiveQuestions();
    return group.questionIds.map(id => effective.find(q => q.id === id)).filter(Boolean);
  };

  const startEdit = (question) => {
    setEditingQuestion(question.id);
    setQuestionDraft({
      text: question.text,
      options: question.options.map(o => ({ ...o })),
    });
  };

  const saveQuestion = async () => {
    if (!questionDraft || !editingQuestion) return;
    setSaving(true);
    try {
      if (!selectedVariant) {
        // Save directly to base template
        const originalQ = baseTemplate.questions.find(q => q.id === editingQuestion);
        const updated = baseTemplate.questions.map(q =>
          q.id === editingQuestion ? { ...q, ...questionDraft } : q
        );
        await updateDoc(doc(db, 'rubricTemplates', baseTemplate.id), { questions: updated });
        // Record history
        await addDoc(collection(db, 'rubricTemplates', baseTemplate.id, 'history'), {
          questionId: editingQuestion, changeType: 'question_text',
          previousText: originalQ?.text, newText: questionDraft.text,
          changedAt: serverTimestamp(), changedBy: 'team',
        });
        setBaseTemplate(prev => ({ ...prev, questions: updated }));
      } else {
        // Save as override in variant
        const overrides = { ...(selectedVariant.questionOverrides || {}), [editingQuestion]: questionDraft };
        await updateDoc(doc(db, 'rubricTemplates', selectedVariant.id), { questionOverrides: overrides });
        setSelectedVariant(prev => ({ ...prev, questionOverrides: overrides }));
        setVariants(prev => prev.map(v =>
          v.id === selectedVariant.id ? { ...v, questionOverrides: overrides } : v
        ));
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      setEditingQuestion(null);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const createVariant = async () => {
    if (!newVariantName.trim()) return;
    setSaving(true);
    try {
      const variantData = {
        isVariant: true,
        baseTemplateId: 'tmpl_prog_rfc_ethiopia_2026',
        name: newVariantName.trim(),
        context: newVariantContext.trim(),
        questionOverrides: {},
        maxScore: 160,
        createdAt: new Date(),
      };
      const ref = await addDoc(collection(db, 'rubricTemplates'), variantData);
      const newVariant = { id: ref.id, ...variantData };
      setVariants(prev => [...prev, newVariant]);
      setSelectedVariant(newVariant);
      setShowNewVariant(false);
      setNewVariantName('');
      setNewVariantContext('');
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const assignVariantToCohort = async (variantId, targetCohortId) => {
    if (!targetCohortId) return;
    try {
      await updateDoc(doc(db, 'cohorts', targetCohortId), { rubricTemplateId: variantId });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) { console.error(err); }
  };

  const resetOverride = async (questionId) => {
    if (!selectedVariant) return;
    const overrides = { ...(selectedVariant.questionOverrides || {}) };
    delete overrides[questionId];
    await updateDoc(doc(db, 'rubricTemplates', selectedVariant.id), { questionOverrides: overrides });
    setSelectedVariant(prev => ({ ...prev, questionOverrides: overrides }));
    setVariants(prev => prev.map(v =>
      v.id === selectedVariant.id ? { ...v, questionOverrides: overrides } : v
    ));
  };

  if (loading) return <div className="gm-loading"><div className="gm-spinner" /></div>;

  return (
    <div className="gm-rubric-editor">
      {/* Header */}
      <div className="gm-rubric-editor-header">
        <div>
          <h2 className="gm-section-title" style={{ marginBottom: 4 }}>Assessment rubric</h2>
          <p style={{ fontSize: '0.82rem', color: '#6b6b6b' }}>
            16 questions · 0–160 scale · 4 stages · Scores locked, text customisable per context
          </p>
        </div>
        {saved && <span className="gm-save-success">✓ Saved</span>}
      </div>

      {/* Variant selector */}
      <div className="gm-rubric-variant-bar">
        <div className="gm-rubric-variant-label">Context variant:</div>
        <button
          className={`gm-rubric-variant-btn ${!selectedVariant ? 'gm-rubric-variant-btn--active' : ''}`}
          onClick={() => setSelectedVariant(null)}
        >
          Base (generic)
        </button>
        {variants.map(v => (
          <button key={v.id}
            className={`gm-rubric-variant-btn ${selectedVariant?.id === v.id ? 'gm-rubric-variant-btn--active' : ''}`}
            onClick={() => setSelectedVariant(v)}
          >
            {v.name}
          </button>
        ))}
        <button className="gm-btn gm-btn-outline gm-btn-sm" onClick={() => setShowNewVariant(true)}>
          + New variant
        </button>
      </div>

      {/* Explicit assignment — always requires picking a cohort by name, never inferred silently */}
      {selectedVariant && (
        <div className="gm-config-card" style={{ marginBottom: 16, marginTop: 4 }}>
          <div className="gm-config-label">Assign "{selectedVariant.name}" to a cohort</div>
          <p className="gm-config-desc">
            Choose exactly which cohort should use this variant. This does not depend on any filter elsewhere on the page.
          </p>
          <div style={{ display: 'flex', gap: 12, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={assignTargetCohortId}
              onChange={e => { setAssignTargetCohortId(e.target.value); setAssignSuccess(false); }}
              style={{ padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: '0.875rem', fontFamily: 'inherit', minWidth: 260 }}
            >
              <option value="">— Select cohort —</option>
              {allCohorts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button className="gm-btn gm-btn-primary gm-btn-sm"
              disabled={!assignTargetCohortId}
              onClick={async () => {
                await assignVariantToCohort(selectedVariant.id, assignTargetCohortId);
                setAssignSuccess(true);
              }}>
              Assign to selected cohort
            </button>
            {assignSuccess && (
              <span className="gm-save-success">
                ✓ Assigned to {allCohorts.find(c => c.id === assignTargetCohortId)?.name}
              </span>
            )}
          </div>
        </div>
      )}

      {selectedVariant && (
        <div className="gm-rubric-variant-info">
          <strong>{selectedVariant.name}</strong>
          {selectedVariant.context && <span> · {selectedVariant.context}</span>}
          <span style={{ color: '#94a3b8' }}> · {Object.keys(selectedVariant.questionOverrides || {}).length} questions customised</span>
        </div>
      )}

      {/* New variant form */}
      {showNewVariant && (
        <div className="gm-config-card" style={{ marginBottom: 16 }}>
          <div className="gm-config-label">Create new context variant</div>
          <p className="gm-config-desc">A variant starts as a copy of the base rubric. You then customise individual question texts and answer labels without changing scores.</p>
          <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
            <input type="text" placeholder="Variant name (e.g. Fintech West Africa)"
              value={newVariantName} onChange={e => setNewVariantName(e.target.value)}
              style={{ padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: '0.875rem', fontFamily: 'inherit', flex: 1, minWidth: 200 }}
            />
            <input type="text" placeholder="Context notes (e.g. B2C fintech, Ghana/Nigeria)"
              value={newVariantContext} onChange={e => setNewVariantContext(e.target.value)}
              style={{ padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: '0.875rem', fontFamily: 'inherit', flex: 1, minWidth: 200 }}
            />
            <button className="gm-btn gm-btn-primary gm-btn-sm" onClick={createVariant} disabled={saving || !newVariantName.trim()}>
              Create
            </button>
            <button className="gm-btn gm-btn-outline gm-btn-sm" onClick={() => setShowNewVariant(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Dimension groups */}
      {DIMENSION_GROUPS.map(group => {
        const groupQuestions = getQuestionsForGroup(group);
        const isExpanded = expandedGroup === group.id;
        const overriddenCount = groupQuestions.filter(q => q.isOverridden).length;

        return (
          <div key={group.id} className="gm-rubric-group">
            <div className="gm-rubric-group-header"
              onClick={() => setExpandedGroup(isExpanded ? null : group.id)}
              style={{ borderLeft: `4px solid ${group.color}` }}>
              <div>
                <div className="gm-rubric-group-title" style={{ color: group.color }}>{group.label}</div>
                <div className="gm-rubric-group-desc">{group.description}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {overriddenCount > 0 && (
                  <span className="gm-pending-badge" style={{ background: '#eff6ff', color: '#0369a1', borderColor: '#93c5fd' }}>
                    {overriddenCount} customised
                  </span>
                )}
                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>4 questions</span>
                <span className="gm-session-toggle">{isExpanded ? '▲' : '▼'}</span>
              </div>
            </div>

            {isExpanded && (
              <div className="gm-rubric-group-body">
                {groupQuestions.map((q, idx) => (
                  <div key={q.id} className={`gm-rubric-question ${q.isOverridden ? 'gm-rubric-question--overridden' : ''}`}>
                    {editingQuestion === q.id ? (
                      // Edit mode
                      <div className="gm-rubric-edit-form">
                        <div className="gm-form-group">
                          <label>Question text</label>
                          <textarea rows={2} value={questionDraft.text}
                            onChange={e => setQuestionDraft(prev => ({ ...prev, text: e.target.value }))}
                          />
                        </div>
                        <div className="gm-rubric-options-grid">
                          {questionDraft.options.map((opt, oi) => (
                            <div key={oi} className="gm-rubric-option-edit">
                              <div className="gm-rubric-option-score">{opt.score} pts</div>
                              <input type="text" value={opt.label}
                                onChange={e => {
                                  const opts = [...questionDraft.options];
                                  opts[oi] = { ...opts[oi], label: e.target.value };
                                  setQuestionDraft(prev => ({ ...prev, options: opts }));
                                }}
                                placeholder={`Answer label for ${opt.score} pts`}
                              />
                            </div>
                          ))}
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                          <button className="gm-btn gm-btn-primary gm-btn-sm" onClick={saveQuestion} disabled={saving}>
                            {saving ? 'Saving…' : 'Save'}
                          </button>
                          <button className="gm-btn gm-btn-outline gm-btn-sm" onClick={() => setEditingQuestion(null)}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      // View mode
                      <div className="gm-rubric-question-view">
                        <div className="gm-rubric-question-header">
                          <div>
                            <span className="gm-rubric-question-num">Q{String(idx + 1 + DIMENSION_GROUPS.findIndex(g => g.id === group.id) * 4).padStart(2,'0')}</span>
                            <span className="gm-rubric-question-dimension">{q.dimension}</span>
                            {q.isOverridden && <span className="gm-validated-badge" style={{ fontSize: '0.65rem' }}>Customised</span>}
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button className="gm-btn gm-btn-sm gm-btn-outline" onClick={() => startEdit(q)}>
                              Edit
                            </button>
                            {q.isOverridden && selectedVariant && (
                              <button className="gm-btn gm-btn-sm gm-btn-outline"
                                style={{ color: '#dc2626', borderColor: '#fecaca' }}
                                onClick={() => resetOverride(q.id)}>
                                Reset
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="gm-rubric-question-text">{q.text}</div>
                        <div className="gm-rubric-options-row">
                          {q.options.map(opt => (
                            <div key={opt.score} className="gm-rubric-option-chip">
                              <span className="gm-rubric-option-pts">{opt.score}</span>
                              <span>{opt.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
